/* oxlint-disable max-lines-per-function, complexity */
/* oxlint-disable jsx-a11y/anchor-is-valid, jsx-a11y/no-noninteractive-element-to-interactive-role */
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGraduationCap,
    faMagnifyingGlass,
    faEarthAmericas,
    faSquareList,
    faFileImport,
    faUserRobot,
} from '@fortawesome/pro-solid-svg-icons';
import { useAngularContext } from 'AngularContext';
import { AI_ADVISOR_BRAND_NAME, hasBotPageAccess as checkBotPageAccess } from 'TutorBotAiAdvisorChat';
import {
    type CurrentUser,
    hasAdminAccess as checkAdminAccess,
    hasEditorAccess as checkEditorAccess,
    hasInterviewerAccess as checkInterviewerAccess,
    hasSuperEditorOrInterviewerAccess as checkSuperEditorOrInterviewerAccess,
    hasReportsAccess as checkReportsAccess,
    getCanCurrentlySubmitProgramApplication,
    getMissingRequiredStudentNetworkEmail,
    getUserProgramState,
    userActionsApi,
} from 'Users';
import { hasGradingPortalAccess as checkGradingPortalAccess } from 'GradingPortal';
import { getProgramTypeConfig } from 'Program';
import { useAppHeader } from '../../hooks';
import { type NavItem, type AdminMenuItem, type AppMenuPresentationProps } from './AppMenu.types';

// Helper to generate nav item classes
// When dense (6+ items), use min-[1065px] breakpoint instead of md for larger sizes
const getNavItemClasses = (isActiveState: boolean, isDisabled: boolean, dense: boolean) =>
    clsx(
        'flex',
        'items-center',
        'gap-2',
        'px-1',
        'py-2',
        'text-[16px]',
        dense ? 'min-[1065px]:text-[18px]' : 'md:text-[18px]',
        'font-medium',
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
        'transition-all',
        'duration-200',
        'whitespace-nowrap',
        isActiveState ? 'text-primary' : 'text-black',
        !isActiveState && !isDisabled && 'hover:text-primary',
        isDisabled && 'opacity-40',
    );

// Helper to generate popover menu item classes (matches Angular .app-header .popover p styles)
const getPopoverItemClasses = (isActiveState: boolean) =>
    clsx(
        // Base styles matching Angular: margin: 0, font-size: 15px, line-height: 30px
        'm-0',
        'text-[15px]',
        'leading-[30px]',
        'pr-5', // padding-right: 20px
        'rtl:pr-0',
        'rtl:pl-5', // RTL: padding-left: 20px
        'cursor-pointer',
        'transition-[color]',
        'duration-[250ms]',
        // Hover: color changes to primary (not background)
        'hover:text-primary',
        // Active state: primary
        isActiveState && 'text-primary',
    );

// Presentation component - handles rendering and sliding indicator logic
export function AppMenuPresentation({
    layout,
    navItems,
    isActive,
    showAdminMenu,
    adminMenuItems,
    adminPopoverIsOpen,
    onNavItemClick,
    onAdminPopoverToggle,
    onClearStreamProgress,
    adminMenuRef,
}: AppMenuPresentationProps) {
    const { t, i18n } = useTranslation();

    // Refs for measuring nav item positions for the sliding indicator
    const navContainerRef = useRef<HTMLElement>(null);
    const navItemRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
    const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null);
    const prevActiveNavKeyRef = useRef<string | null>(null);

    // Find the active nav item key
    const activeNavKey = useMemo(() => {
        const activeItem = navItems.find(item => isActive(item.routes));
        return activeItem?.key ?? null;
    }, [navItems, isActive]);

    // Update the sliding indicator position when active item changes
    const updateIndicatorPosition = useCallback(() => {
        if (!activeNavKey || !navContainerRef.current) {
            // Don't reset position - keep previous position for smooth transitions
            return;
        }

        const activeElement = navItemRefs.current.get(activeNavKey);
        if (!activeElement) {
            // Don't reset position - keep previous position for smooth transitions
            return;
        }

        // Use offsetLeft instead of getBoundingClientRect for more stable positioning
        // during layout shifts
        setIndicatorStyle({
            left: activeElement.offsetLeft,
            width: activeElement.offsetWidth,
        });
    }, [activeNavKey]);

    // Update indicator on active change and on mount
    // Only update when activeNavKey actually changes, not on every re-render
    useEffect(() => {
        if (prevActiveNavKeyRef.current === activeNavKey) return undefined;
        const isInitialMount = prevActiveNavKeyRef.current === null;
        prevActiveNavKeyRef.current = activeNavKey;

        if (isInitialMount) {
            // On initial mount, set position immediately without transition
            updateIndicatorPosition();
            return undefined;
        }

        // Longer delay when navigating to Home — the dashboard route triggers layout
        // shifts (rounded corners, content load) that can cause the indicator to stutter.
        const delay = activeNavKey === 'home' ? 150 : 50;
        const timeoutId = setTimeout(() => {
            updateIndicatorPosition();
        }, delay);

        return () => clearTimeout(timeoutId);
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [activeNavKey]);

    // Also update on window resize (debounced to avoid interference with navigation)
    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(updateIndicatorPosition, 200);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, [updateIndicatorPosition]);

    // Helper to set refs
    const setNavItemRef = useCallback(
        (key: string) => (el: HTMLDivElement | null) => {
            navItemRefs.current.set(key, el);
        },
        [],
    );

    if (layout !== 'default') return null;

    const hasAdminAccess = adminMenuItems.some(item => item.show);
    const dense = navItems.length >= 6 || i18n?.language === 'am';

    return (
        <nav
            ref={navContainerRef}
            className={clsx(
                'app-menu',
                hasAdminAccess && 'admin-menu',
                // Tailwind
                'subpixel-antialiased',
                'relative',
                'flex',
                'items-center',
                'gap-0',
                dense ? 'min-[1065px]:gap-1' : 'md:gap-1',
                'h-full',
            )}
        >
            {/* Navigation items */}
            {navItems.map(item => (
                <div
                    key={item.key}
                    ref={setNavItemRef(item.key)}
                    data-name={item.key}
                    className={clsx(
                        'relative',
                        'transition-all',
                        'duration-200',
                        'me-[9px]',
                        'min-[830px]:me-4',
                        dense ? 'min-[1065px]:me-6' : 'md:me-6',
                    )}
                >
                    <a
                        className={getNavItemClasses(isActive(item.routes), item.disabled ?? false, dense)}
                        href="#"
                        onClick={e => !(item.disabled ?? false) && onNavItemClick(item.route, e)}
                    >
                        {/* Icons kept in config for mobile menu but hidden on desktop */}
                        <FontAwesomeIcon icon={item.icon} className="hidden" />
                        <span>{item.label}</span>
                    </a>
                    {item.badge && (
                        <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue text-[10px] font-bold text-white">
                            1
                        </div>
                    )}
                </div>
            ))}

            {/* Admin Tools (if user has access) */}
            {showAdminMenu && (
                <li
                    ref={adminMenuRef}
                    data-name="admin-menu"
                    className={clsx('admin-menu-item', 'relative', 'list-none')}
                >
                    <a className={getNavItemClasses(false, false, dense)} href="#" onClick={onAdminPopoverToggle}>
                        {t('navigation.app_menu.admin_tools')}
                    </a>

                    {adminPopoverIsOpen && (
                        <div className="admin-popover absolute right-0 top-full z-[10001] mt-2.5 whitespace-nowrap rounded-lg border-none bg-white font-normal text-slate-grey shadow-[0_0_10px_0_rgba(0,0,0,0.2)] rtl:left-0 rtl:right-auto">
                            {/* Arrow pointing up - matches user menu arrow style */}
                            <div className="absolute -top-2 right-[30px] h-0 w-0 border-b-[8px] border-l-[8px] border-r-[8px] border-t-0 border-solid border-b-white border-l-transparent border-r-transparent rtl:left-[30px] rtl:right-auto" />
                            <nav className="flex flex-col divide-y divide-solid divide-beige px-4 py-2">
                                {/* Groups match Angular admin-popover.html: dividers between groups, not items */}
                                {[
                                    ['grading_portal'],
                                    ['edit_content'],
                                    ['manage_users', 'batch_edit_users', 'mba', 'manage_global_metadata'],
                                    ['reports'],
                                    ['clear_progress'],
                                ].map(groupKeys => {
                                    const groupItems = groupKeys
                                        .map(key => adminMenuItems.find(item => item.key === key))
                                        .filter((item): item is AdminMenuItem => !!item && item.show);
                                    if (groupItems.length === 0) return null;
                                    return (
                                        <div key={groupKeys[0]} className="border-x-0 py-1 first:pt-0 last:pb-0">
                                            {groupItems.map(item => {
                                                const specialHandlers: Record<string, () => void> = {
                                                    clear_progress: onClearStreamProgress,
                                                };
                                                const handler = specialHandlers[item.key];
                                                if (handler) {
                                                    return (
                                                        <p
                                                            key={item.key}
                                                            onClick={handler}
                                                            className={getPopoverItemClasses(false)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    handler();
                                                                }
                                                            }}
                                                        >
                                                            {item.label}
                                                        </p>
                                                    );
                                                }
                                                return (
                                                    <p
                                                        key={item.key}
                                                        onClick={e => onNavItemClick(item.route, e)}
                                                        className={getPopoverItemClasses(isActive([item.route]))}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                onNavItemClick(item.route);
                                                            }
                                                        }}
                                                    >
                                                        {item.label}
                                                    </p>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </nav>
                        </div>
                    )}
                </li>
            )}

            {/* Sliding indicator bar */}
            <div
                className={clsx(
                    'absolute',
                    '-bottom-px',
                    'z-[1]',
                    'h-[3px]',
                    'bg-primary',
                    'rounded-t-full',
                    'transition-all',
                    'duration-300',
                    'ease-in-out',
                    (!indicatorStyle || !activeNavKey) && 'opacity-0',
                )}
                style={{
                    left: indicatorStyle?.left ?? 0,
                    width: indicatorStyle?.width ?? 0,
                }}
            />
        </nav>
    );
}

// Container component - handles hooks and state, delegates rendering to presentation
export function AppMenu() {
    const { t } = useTranslation();
    const angularContext = useAngularContext();

    const { layout, inOfflineMode, isCordova, currentUser, loadRoute, isActive } = useAppHeader();
    const typedCurrentUser = currentUser as CurrentUser | null;

    const [popoverIsOpen, setPopoverIsOpen] = useState(false);
    const adminMenuRef = useRef<HTMLLIElement>(null);
    const [clearAllProgress] = userActionsApi.useClearAllProgressMutation();

    const hasAdminAccess = checkAdminAccess(typedCurrentUser);
    const hasStudentNetworkAccess = typedCurrentUser?.hasStudentNetworkAccess ?? false;
    const hasAccessToFullStudentNetworkProfiles = typedCurrentUser?.hasAccessToFullStudentNetworkProfiles ?? false;
    const hasSeenStudentNetwork = typedCurrentUser?.hasSeenStudentNetwork ?? true;
    const missingRequiredStudentNetworkEmail = getMissingRequiredStudentNetworkEmail(typedCurrentUser);
    const programTypeConfig = getProgramTypeConfig(getUserProgramState(typedCurrentUser)?.programType);
    const supportsResourcesTab = !!programTypeConfig?.supportsResourcesTab;
    const hasBotPageAccess = typedCurrentUser ? checkBotPageAccess({ currentUser: typedCurrentUser }) : false;
    const canCurrentlySubmitProgramApplication = getCanCurrentlySubmitProgramApplication(typedCurrentUser);

    // Admin access checks
    const hasGradingPortalAccess = checkGradingPortalAccess(typedCurrentUser);
    const hasEditorAccess = checkEditorAccess(typedCurrentUser);
    const hasInterviewerAccess = checkInterviewerAccess(typedCurrentUser);
    const hasSuperEditorOrInterviewerAccess = checkSuperEditorOrInterviewerAccess(typedCurrentUser);
    const hasReportsAccess = checkReportsAccess(typedCurrentUser);

    const showStudentNetworkBadge =
        hasAccessToFullStudentNetworkProfiles && (!hasSeenStudentNetwork || missingRequiredStudentNetworkEmail);

    const showAdminMenu = useMemo(
        () =>
            !isCordova &&
            !inOfflineMode &&
            currentUser &&
            (hasGradingPortalAccess || hasEditorAccess || hasSuperEditorOrInterviewerAccess || hasReportsAccess),
        [
            isCordova,
            inOfflineMode,
            currentUser,
            hasGradingPortalAccess,
            hasEditorAccess,
            hasSuperEditorOrInterviewerAccess,
            hasReportsAccess,
        ],
    );

    // Build navigation items array
    const navItems: NavItem[] = useMemo(() => {
        const items: NavItem[] = [
            {
                key: 'home',
                label: t('navigation.app_menu.home'),
                icon: faGraduationCap,
                routes: ['/dashboard'],
                route: '/dashboard',
            },
            {
                key: 'courses',
                label: t('navigation.app_menu.courses'),
                icon: faMagnifyingGlass,
                routes: ['/courses'],
                route: '/courses',
                disabled: inOfflineMode,
            },
            {
                key: 'ai-advisor',
                label: AI_ADVISOR_BRAND_NAME,
                icon: faUserRobot,
                routes: ['/bot'],
                route: '/bot',
                disabled: inOfflineMode,
            },
            {
                key: 'student-network',
                label: t('navigation.app_menu.student_network'),
                icon: faEarthAmericas,
                routes: ['/student-network'],
                route: '/student-network',
                disabled: inOfflineMode,
                badge: showStudentNetworkBadge,
            },
            {
                key: 'resources',
                label: t('navigation.app_menu.resources'),
                icon: faSquareList,
                routes: ['/resources'],
                route: '/resources',
                disabled: inOfflineMode,
            },
            {
                key: 'application',
                label: t('navigation.app_menu.application'),
                icon: faFileImport,
                routes: ['/settings/application'],
                route: '/settings/application',
                disabled: inOfflineMode,
            },
        ];

        // Filter based on user permissions
        return items.filter(item => {
            switch (item.key) {
                case 'home':
                case 'courses':
                    return true;
                case 'ai-advisor':
                    return hasBotPageAccess;
                case 'student-network':
                    return hasStudentNetworkAccess;
                case 'resources':
                    return supportsResourcesTab;
                case 'application':
                    return canCurrentlySubmitProgramApplication;
                default:
                    return false;
            }
        });
    }, [
        t,
        inOfflineMode,
        hasBotPageAccess,
        hasStudentNetworkAccess,
        showStudentNetworkBadge,
        supportsResourcesTab,
        canCurrentlySubmitProgramApplication,
    ]);

    // Build admin menu items
    const adminMenuItems: AdminMenuItem[] = useMemo(
        () => [
            {
                key: 'grading_portal',
                label: t('navigation.app_menu.grading_portal'),
                route: '/grading-portal',
                show: hasGradingPortalAccess,
            },
            {
                key: 'edit_content',
                label: t('navigation.app_menu.edit_content'),
                route: '/editor',
                show: hasEditorAccess,
            },
            {
                key: 'manage_users',
                label: t('navigation.app_menu.manage_users'),
                route: '/admin/users',
                show: hasInterviewerAccess,
            },
            {
                key: 'batch_edit_users',
                label: t('navigation.app_menu.batch_edit_users'),
                route: '/admin/batch-users',
                show: hasAdminAccess,
            },
            {
                key: 'mba',
                label: t('navigation.app_menu.mba'),
                route: '/admin/mba',
                show: hasAdminAccess,
            },
            {
                key: 'manage_global_metadata',
                label: t('navigation.app_menu.manage_global_metadata'),
                route: '/admin/global-metadata',
                show: hasAdminAccess,
            },
            {
                key: 'reports',
                label: t('navigation.app_menu.reports'),
                route: '/reports',
                show: hasReportsAccess,
            },
            {
                key: 'clear_progress',
                label: t('navigation.app_menu.clear_progress'),
                route: '',
                show: hasEditorAccess,
            },
        ],
        // oxlint-disable-next-line react-hooks/exhaustive-deps
        [t, hasGradingPortalAccess, hasEditorAccess, hasInterviewerAccess, hasAdminAccess, hasReportsAccess],
    );

    const goToRoute = useCallback(
        (route: string, event?: React.MouseEvent) => {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            setPopoverIsOpen(false);
            loadRoute(route);
        },
        [loadRoute],
    );

    const clearStreamProgress = useCallback(async () => {
        const confirmed = window.confirm(t('navigation.app_menu.clear_progress_confirm'));
        if (confirmed && typedCurrentUser) {
            await clearAllProgress({ userId: typedCurrentUser.id }).unwrap();
            loadRoute('/courses');

            const reactRouterBridge = angularContext?.get('reactRouterBridge') as
                | { bumpRouteReloadKey: () => void }
                | undefined;
            if (reactRouterBridge) {
                reactRouterBridge.bumpRouteReloadKey();
            }

            // Show success toast
            const ngToast = angularContext?.get('ngToast') as
                | { create: (opts: { content: string; className: string }) => void }
                | undefined;
            if (ngToast) {
                ngToast.create({
                    content: t('navigation.app_menu.clear_progress_success'),
                    className: 'success',
                });
            }
        }
    }, [typedCurrentUser, clearAllProgress, loadRoute, angularContext, t]);

    const handleAdminPopoverToggle = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setPopoverIsOpen(prev => !prev);
    }, []);

    // Close popover when clicking outside
    useClickAway(adminMenuRef, () => {
        if (popoverIsOpen) setPopoverIsOpen(false);
    });

    return (
        <AppMenuPresentation
            layout={layout}
            navItems={navItems}
            isActive={isActive}
            showAdminMenu={showAdminMenu ?? false}
            adminMenuItems={adminMenuItems}
            adminPopoverIsOpen={popoverIsOpen}
            onNavItemClick={goToRoute}
            onAdminPopoverToggle={handleAdminPopoverToggle}
            onClearStreamProgress={clearStreamProgress}
            adminMenuRef={adminMenuRef}
        />
    );
}
